import { documentTypeLabel } from './enumUiLabels';

export const CUSTOM_DOCUMENT_SYSTEM_TYPE = 'OTHER';

export const CUSTOM_DOCUMENT_CATEGORY_OPTIONS = [
  { value: 'CLIENT', label: 'Документы клиента' },
  { value: 'CARRIER', label: 'Документы перевозчика' },
  { value: 'INTERNAL', label: 'Внутренние документы' },
  { value: 'GENERAL', label: 'Прочее' },
] as const;

export type DocumentDisplayNameSource = {
  documentType?: string | null;
  customDocumentName?: string | null;
  typeLabel?: string | null;
  fileName?: string | null;
  content?: { originalName?: unknown } | null;
};

const INTERNAL_STORAGE_NAME = /^MANUAL_\d/i;

export function isInternalStorageFileName(value: string | null | undefined): boolean {
  return INTERNAL_STORAGE_NAME.test(String(value || '').trim());
}

export function isInternalCompositionItemKey(value: string | null | undefined): boolean {
  return isInternalStorageFileName(value) || String(value || '').trim().startsWith('MANUAL_');
}

function readOriginalFileName(doc: DocumentDisplayNameSource | null | undefined): string {
  const fromContent =
    doc?.content &&
    typeof doc.content === 'object' &&
    typeof doc.content.originalName === 'string'
      ? String(doc.content.originalName).trim()
      : '';
  if (fromContent && !isInternalStorageFileName(fromContent)) {
    return fromContent;
  }

  const fromFileName = String(doc?.fileName ?? '').trim();
  if (fromFileName && !isInternalStorageFileName(fromFileName)) {
    return fromFileName;
  }

  return '';
}

/**
 * User-facing document title (DOC-013A):
 * customDocumentName → standard type label → original file name; never storage filename.
 */
export function resolveDocumentUserFacingName(
  doc: DocumentDisplayNameSource | null | undefined
): string {
  if (!doc) return '—';

  const custom = String(doc.customDocumentName ?? '').trim();
  if (custom) return custom;

  const typeLabel = resolveDocumentTypeLabel(doc);
  if (typeLabel && typeLabel !== '—') return typeLabel;

  const explicitType = String(doc.typeLabel ?? '').trim();
  if (explicitType && explicitType !== '—' && explicitType.toUpperCase() !== 'OTHER') {
    return explicitType;
  }

  const original = readOriginalFileName(doc);
  if (original) return original;

  return '—';
}

/** Same priority as {@link resolveDocumentUserFacingName} for file column / picker name. */
export function resolveDocumentUserFacingFileName(
  doc: DocumentDisplayNameSource | null | undefined
): string {
  return resolveDocumentUserFacingName(doc);
}

/** Composition row title: keep catalog slot labels; hide MANUAL_* keys; use user-facing doc names for manual/custom. */
export function resolveCompositionItemDocumentLabel(
  itemLabel: string,
  document: DocumentDisplayNameSource | null | undefined
): string {
  const label = String(itemLabel || '').trim();

  if (document) {
    const custom = String(document.customDocumentName ?? '').trim();
    if (custom) return custom;
  }

  if (isInternalCompositionItemKey(label)) {
    if (document) {
      const fromDocument = resolveDocumentUserFacingName(document);
      if (fromDocument !== '—') return fromDocument;
    }
    return '—';
  }

  if (label) return label;

  if (document) {
    const fromDocument = resolveDocumentUserFacingName(document);
    if (fromDocument !== '—') return fromDocument;
  }

  return '—';
}

export function resolveCompositionItemFileDisplayName(
  document: DocumentDisplayNameSource | null | undefined
): string | null {
  if (!document) return null;
  const original = readOriginalFileName(document);
  if (original) return original;
  const custom = String(document.customDocumentName ?? '').trim();
  return custom || null;
}

export type CustomDocumentCategoryValue = (typeof CUSTOM_DOCUMENT_CATEGORY_OPTIONS)[number]['value'];

export type CustomDocumentUploadContext = {
  application?: unknown | null;
  route?: unknown | null;
  cargo?: unknown | null;
  entityType?: string | null;
};

/** Infer customDocumentCategory from upload host context (DOC-012A). */
export function resolveCustomDocumentCategoryFromUploadContext(
  context: CustomDocumentUploadContext
): CustomDocumentCategoryValue {
  if (context.route) return 'CARRIER';
  if (context.application) return 'CLIENT';
  if (context.cargo || context.entityType) return 'INTERNAL';
  return 'GENERAL';
}

/** Basename without extension for auto-fill of custom document name. */
export function stripFileExtension(filename: string): string {
  const base = String(filename || '').trim();
  if (!base) return '';
  const lastSlash = Math.max(base.lastIndexOf('/'), base.lastIndexOf('\\'));
  const name = lastSlash >= 0 ? base.slice(lastSlash + 1) : base;
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return name;
  return name.slice(0, dot);
}

/** Unified document type label for tables, pickers, and drawers. */
export function resolveDocumentTypeLabel(doc: DocumentDisplayNameSource): string {
  return resolveDocumentDisplayTypeLabel(doc, documentTypeLabel);
}

/** Display label for document type column; OTHER → customDocumentName (never raw OTHER). */
export function resolveDocumentDisplayTypeLabel(
  doc: DocumentDisplayNameSource,
  fallbackLabel: (documentType: string) => string
): string {
  const docType = String(doc.documentType ?? '')
    .trim()
    .toUpperCase();
  if (docType === CUSTOM_DOCUMENT_SYSTEM_TYPE) {
    const custom = String(doc.customDocumentName ?? '').trim();
    return custom || '—';
  }
  return fallbackLabel(doc.documentType ?? '');
}
